import { itemDef, stackOf } from "./items.ts";
import { DEATH_STRUCTURE_NAMES } from "./death.ts";
import { MANDINGA_STRUCTURE_NAMES } from "./mandinga.ts";
import { POI_STRUCTURE_NAMES } from "./pois.ts";
import { simpatiaChestOf } from "./simpatias.ts";

/**
 * Baús e tabelas de loot (doc 03: Armazenamento compartilhado; Baús naturais e mapas de objetivo;
 * Tabelas de loot e multiplicadores; Loot de exemplo). Dados e funções puras: o sorteio recebe a
 * fonte de aleatoriedade (o servidor passa um PRNG da seed do baú).
 */

/** Slots de qualquer baú (o de Madeira e os naturais). */
export const CHEST_SLOTS = 18;

/** Item que, posicionado, é um baú (armazenamento sem processo). */
export const STORAGE_ITEMS: Record<string, number> = { "bau-madeira": CHEST_SLOTS };

/** Moeda: um item que só passa pelo chão e pelos baús e vira saldo na bolsa ao chegar no inventário. */
export const COIN_ITEM = "tostao";

export interface LootEntry {
  /** Item, ou um sorteio uniforme entre vários (a "peça de Couro aleatória"). */
  item: string | readonly string[];
  min: number;
  max: number;
  /** Chance independente (rolagens) ou a fatia do "um de". Ausente nos garantidos. */
  chance?: number;
  /** Achado lendário: anunciado a todos no HUD. */
  rare?: boolean;
}

export interface LootTable {
  id: string;
  guaranteed: readonly LootEntry[];
  rolls: readonly LootEntry[];
  /** No máximo uma destas sai (chances somadas ≤ 1). */
  oneOf: readonly LootEntry[];
}

/** Garantido só no modo da Jangada (os mapas são do objetivo do Mutirão). */
export const MUIRAQUITA_MAP = "mapa-muiraquitas";
export const RAFT_MAP = "mapa-jangada";

const LEATHER = ["capacete-couro", "gibao-couro", "perneiras-couro", "botas-vaqueiro"] as const;

/** Tabelas do doc 03 ("Loot de exemplo"). */
export const LOOT_TABLES: Record<string, LootTable> = {
  acampamento: {
    id: "acampamento",
    guaranteed: [{ item: COIN_ITEM, min: 5, max: 15 }],
    rolls: [
      { item: "mandioca", min: 2, max: 5, chance: 0.5 },
      { item: "banana", min: 2, max: 4, chance: 0.5 },
      { item: "flecha-pau", min: 8, max: 16, chance: 0.35 },
      { item: "corda", min: 1, max: 3, chance: 0.4 },
    ],
    oneOf: [{ item: ["baiao", "moqueca", "tapioca"], min: 2, max: 2, chance: 0.2 }],
  },
  engenho: {
    id: "engenho",
    guaranteed: [{ item: COIN_ITEM, min: 10, max: 30 }],
    rolls: [
      { item: "lingote-ferro", min: 2, max: 6, chance: 0.35 },
      { item: "madeira-de-lei", min: 4, max: 8, chance: 0.4 },
      { item: "corda", min: 2, max: 4, chance: 0.4 },
      { item: "tocha", min: 2, max: 4, chance: 0.3 },
    ],
    oneOf: [
      { item: LEATHER, min: 1, max: 1, chance: 0.15 },
      { item: "facao-ferro", min: 1, max: 1, chance: 0.08, rare: true },
    ],
  },
  gruta: {
    id: "gruta",
    guaranteed: [{ item: "minerio-ferro", min: 4, max: 8 }],
    rolls: [
      { item: "carvao", min: 3, max: 6, chance: 0.4 },
      { item: "minerio-prata", min: 2, max: 5, chance: 0.2 },
      { item: "pederneira", min: 2, max: 4, chance: 0.3 },
      { item: COIN_ITEM, min: 8, max: 20, chance: 0.4 },
    ],
    oneOf: [
      { item: "picareta-pedra", min: 1, max: 1, chance: 0.15 },
      { item: "picareta-ferro", min: 1, max: 1, chance: 0.04, rare: true },
    ],
  },
  destrocos: {
    id: "destrocos",
    // O Mapa dos Muiraquitãs entra à parte, só no Mutirão (`MUIRAQUITA_MAP`).
    guaranteed: [],
    rolls: [
      { item: "corda", min: 4, max: 8, chance: 0.7 },
      { item: "madeira-de-lei", min: 6, max: 10, chance: 0.6 },
      { item: "resina-jatoba", min: 2, max: 4, chance: 0.4 },
    ],
    oneOf: [],
  },
};

export type NaturalChestKind = "acampamento" | "engenho" | "gruta" | "destrocos";

/** Baús naturais: estruturas do mundo (não são itens), com a tabela pelo tipo. */
export const NATURAL_CHESTS: Record<NaturalChestKind, { item: string; name: string; table: string }> = {
  acampamento: { item: "bau-acampamento", name: "Baú do Acampamento", table: "acampamento" },
  engenho: { item: "bau-engenho", name: "Baú da Fortaleza", table: "engenho" },
  gruta: { item: "bau-gruta", name: "Baú da Caverna", table: "gruta" },
  destrocos: { item: "bau-destrocos", name: "Baú do Naufrágio", table: "destrocos" },
};

const NATURAL_BY_ITEM = new Map(Object.values(NATURAL_CHESTS).map((c) => [c.item, c]));

/** Baú natural pela estrutura (`itemId` da `Structure`), ou `undefined`. */
export const naturalChestOf = (itemId: string): { item: string; name: string; table: string } | undefined => NATURAL_BY_ITEM.get(itemId);

/** Estrutura do mundo (não posicionada por jogador): Mascate. Os baús entram por `worldStructure`. */
export const MASCATE_ITEM = "mascate";

/** Nome de uma estrutura: o do item posicionado, do baú natural, do Baú de Simpatia, dos POIs ou "Mercador". */
export const structureName = (itemId: string): string =>
  itemDef(itemId)?.name ?? naturalChestOf(itemId)?.name ?? simpatiaChestOf(itemId)?.name ?? POI_STRUCTURE_NAMES[itemId] ?? DEATH_STRUCTURE_NAMES[itemId] ?? MANDINGA_STRUCTURE_NAMES[itemId] ?? (itemId === MASCATE_ITEM ? "Mercador" : itemId);

/** A estrutura nasce com o mundo (baús, mascates, Carrancas, Pedras de Encanto, santuários), não de um jogador? */
export const worldStructure = (itemId: string): boolean =>
  naturalChestOf(itemId) !== undefined || simpatiaChestOf(itemId) !== undefined || itemId === MASCATE_ITEM || POI_STRUCTURE_NAMES[itemId] !== undefined || DEATH_STRUCTURE_NAMES[itemId] !== undefined || MANDINGA_STRUCTURE_NAMES[itemId] !== undefined;

/** Quantos slots de armazenamento a estrutura tem (baús), ou 0. */
export const storageSlotsOf = (itemId: string): number => STORAGE_ITEMS[itemId] ?? (naturalChestOf(itemId) ? CHEST_SLOTS : itemId === "trouxa" ? TROUXA_SLOTS : 0);

/** A Trouxa guarda o inventário inteiro de quem morreu: até 24 slots, 4 de armadura e a Aljava. */
export const TROUXA_SLOTS = 29;

/**
 * Sorte do grupo no sorteio (Ferradura, F19). `qtyMul` multiplica as quantidades com
 * arredondamento estocástico (2,6 vira 3 com 60 % de chance); `rareBoost` aumenta, em termos
 * relativos, as chances de até 20 % (as raras), com teto de +50 %.
 */
export interface LootLuck {
  qtyMul?: number;
  rareBoost?: number;
}

/** Chances de até 20 % contam como raras para a sorte (premissa do doc 03, "chances raras"). */
const RARE_CHANCE = 0.2;
const RARE_BOOST_CAP = 0.5;

export interface LootStack {
  itemId: string;
  qty: number;
  rare: boolean;
}

/**
 * Sorteia o conteúdo de um baú (doc 03): os garantidos, cada rolagem independente e no máximo um
 * do "um de". Mínimo de 2 entradas: se o sorteio der menos, entram as rolagens mais prováveis que
 * faltaram. Quantidades dentro da pilha do item. `rand` devolve [0, 1).
 */
export function rollLoot(table: LootTable, rand: () => number, luck: LootLuck = {}): LootStack[] {
  const qtyMul = luck.qtyMul ?? 1;
  const boost = Math.min(RARE_BOOST_CAP, Math.max(0, luck.rareBoost ?? 0));
  const chanceOf = (e: LootEntry) => {
    const c = e.chance ?? 1;
    return c <= RARE_CHANCE ? c * (1 + boost) : c;
  };
  const out: LootStack[] = [];
  const take = (e: LootEntry) => {
    const itemId = typeof e.item === "string" ? e.item : (e.item[Math.floor(rand() * e.item.length)] as string);
    const base = e.min + Math.floor(rand() * (e.max - e.min + 1));
    const scaled = base * qtyMul;
    const whole = Math.floor(scaled);
    const qty = Math.min(stackOf(itemId), Math.max(1, whole + (rand() < scaled - whole ? 1 : 0)));
    const same = out.find((s) => s.itemId === itemId);
    if (same) same.qty = Math.min(stackOf(itemId), same.qty + qty);
    else out.push({ itemId, qty, rare: e.rare === true });
  };
  for (const e of table.guaranteed) take(e);
  const missed: LootEntry[] = [];
  for (const e of table.rolls) {
    if (rand() < chanceOf(e)) take(e);
    else missed.push(e);
  }
  if (table.oneOf.length > 0) {
    let u = rand();
    for (const e of table.oneOf) {
      const c = chanceOf(e);
      if (u < c) {
        take(e);
        break;
      }
      u -= c;
    }
  }
  missed.sort((a, b) => (b.chance ?? 0) - (a.chance ?? 0));
  for (const e of missed) {
    if (out.length >= 2) break;
    take(e);
  }
  return out;
}

/** Slots distintos sorteados para as pilhas (sem sobrescrever uma com outra, doc 03). */
export function lootSlots(count: number, slots: number, rand: () => number): number[] {
  const order = Array.from({ length: slots }, (_, i) => i);
  for (let i = 0; i < Math.min(count, slots); i++) {
    const j = i + Math.floor(rand() * (slots - i));
    [order[i], order[j]] = [order[j] as number, order[i] as number];
  }
  return order.slice(0, Math.min(count, slots));
}
